export function isModifierShortcut(event: KeyboardEvent): boolean {
  return (event.metaKey || event.ctrlKey) && !event.altKey;
}

/** Match ⌘/Ctrl + `keyLabel` (e.g. `I`, `E`, `/`). Letter keys are case-insensitive. */
export function matchesModifierShortcutKey(event: KeyboardEvent, keyLabel: string): boolean {
  if (keyLabel.length === 1 && /[a-z]/i.test(keyLabel)) {
    return event.key.toLowerCase() === keyLabel.toLowerCase();
  }

  return event.key === keyLabel;
}

/** WAI-ARIA `aria-keyshortcuts` for cross-platform modifier chords (Control on Windows, Meta on macOS). */
export function modifierKeyAriaShortcut(keyLabel: string, options?: { shift?: boolean }): string {
  const shiftPrefix = options?.shift ? "Shift+" : "";

  if (keyLabel.length === 1 && /[a-z]/i.test(keyLabel)) {
    const upper = keyLabel.toUpperCase();
    return `Control+${shiftPrefix}${upper} Meta+${shiftPrefix}${upper}`;
  }

  return `Control+${shiftPrefix}${keyLabel} Meta+${shiftPrefix}${keyLabel}`;
}

/** ⌘⇧/ (shown as ⌘?) — keyboard shortcuts help. */
export function matchesKeyboardShortcutsHelpShortcut(event: KeyboardEvent): boolean {
  if (!isModifierShortcut(event) || !event.shiftKey) return false;
  return event.key === "?" || event.key === "/";
}

/** ⌘/Ctrl+K or ⌘/Ctrl+Shift+P — Global Search. Shift+K and unmodified P do not match. */
export function matchesGlobalSearchShortcut(event: KeyboardEvent): boolean {
  if (!isModifierShortcut(event)) return false;
  if (event.shiftKey) return matchesModifierShortcutKey(event, "p");
  return matchesModifierShortcutKey(event, "k");
}

/** WAI-ARIA `aria-keyshortcuts` for both global-search chords. */
export function globalSearchAriaShortcut(): string {
  return `${modifierKeyAriaShortcut("k")} ${modifierKeyAriaShortcut("p", { shift: true })}`;
}

export function isEditableKeyboardTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;

  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;

  if (target.closest("md-outlined-text-field, md-filled-text-field")) return true;

  return false;
}

/** Single-key app chords (`d`, `g`, …) — not while typing or when overlays / popups are open. */
export function shouldHandleAppChord(event: KeyboardEvent): boolean {
  if (event.repeat) return false;
  if (event.metaKey || event.ctrlKey || event.altKey) return false;
  if (isEditableKeyboardTarget(event.target)) return false;
  return true;
}
